import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import * as LucideIcons from "lucide-react";
import { ArrowRight, Sparkles, Zap } from "lucide-react";
import { formatTimeSaved, formatTimeSavedHuman } from "@/utils/time.utils";
import { useState } from "react";
import clsx from "clsx";
import { useGetMyTimeSavedQuery } from "@/services/(company)/subscriptionApi";
import { Skeleton } from "@/components/ui/skeleton";

type FilterKey = "1M" | "3M" | "6M" | "1Y" | "ALL";

const buildFilters = (t: TFunction): { label: string; key: FilterKey }[] => [
  { label: "1M", key: "1M" },
  { label: "3M", key: "3M" },
  { label: "6M", key: "6M" },
  { label: "1Y", key: "1Y" },
  { label: t("company:subscription.all_time"), key: "ALL" },
];

function formatMonth(yyyyMm: string): string {
  const [year, month] = yyyyMm.split("-");
  return new Date(Number(year), Number(month) - 1, 1).toLocaleDateString(
    "en-US",
    {
      month: "short",
      year: "numeric",
    },
  );
}

interface Props {
  onViewDetails?: () => void;
}

export function TimeSavedPopoverContent({ onViewDetails }: Props) {
  const { t } = useTranslation(["common"]);
  const [filter, setFilter] = useState<FilterKey>("1M");
  const [showAllMonths, setShowAllMonths] = useState(false);

  const { data, isFetching } = useGetMyTimeSavedQuery(
    filter === "ALL" ? undefined : { period: filter },
  );

  const saved = data?.data;
  const MONTHS_PREVIEW = 4;
  const allMonths = saved?.monthlyHistory ?? [];
  const visibleMonths = showAllMonths
    ? allMonths
    : allMonths.slice(0, MONTHS_PREVIEW);

  const handleFilterChange = (key: FilterKey) => {
    setFilter(key);
    setShowAllMonths(false);
  };

  const totalMins = saved?.totalTimeSavedMinutes ?? 0;
  const workMins = saved?.workingMinutesPerDay;
  const humanTotal = formatTimeSavedHuman(totalMins, workMins);

  return (
    <div className="flex flex-col">
      {/* ── Hero ── */}
      <div className="relative bg-gradient-to-br from-primary/[0.10] via-primary/[0.05] to-transparent px-4 pt-4 pb-3 border-b border-border/40 overflow-hidden">
        {/* subtle background glow */}
        <div className="absolute -top-6 -right-6 size-24 rounded-full bg-primary/10 blur-2xl pointer-events-none" />

        <div className="flex items-start gap-3 relative">
          <img
            src="/icons/interface-icons/shared/icon-3d-clock-up.png"
            alt={t("common:chrome.time_saved")}
            draggable={false}
            className="size-12 object-contain shrink-0 mt-0.5"
          />
          <div className="flex-1 min-w-0">
            <p className="text-[11px] text-muted-foreground font-medium tracking-wide uppercase">
              {t("common:chrome.ai_saved_you")}
            </p>
            {isFetching ? (
              <div className="mt-1 space-y-1.5">
                <Skeleton className="h-8 w-24 rounded" />
                <Skeleton className="h-3 w-32 rounded" />
              </div>
            ) : (
              <>
                <p className="text-3xl font-black text-primary tabular-nums leading-none mt-1">
                  {formatTimeSaved(totalMins)}
                </p>
                {humanTotal && (
                  <p className="text-[11px] text-muted-foreground mt-1 font-medium">
                    {humanTotal}
                  </p>
                )}
              </>
            )}
          </div>

          {/* Uses badge */}
          {!isFetching && (saved?.totalUses ?? 0) > 0 && (
            <div className="shrink-0 flex flex-col items-center gap-0.5 bg-white/80 border border-border/40 rounded-lg px-2.5 py-1.5 shadow-xs">
              <Zap size={11} className="text-primary" />
              <span className="text-base font-black tabular-nums text-foreground leading-none">
                {saved?.totalUses}
              </span>
              <span className="text-[9px] text-muted-foreground font-medium leading-none">
                {t("common:chrome.uses")}
              </span>
            </div>
          )}
          {isFetching && <Skeleton className="size-14 rounded-lg shrink-0" />}
        </div>

        {/* Period filter chips */}
        <div className="flex gap-1 flex-wrap mt-3">
          {buildFilters(t).map((f) => (
            <button
              key={f.key}
              onClick={() => handleFilterChange(f.key)}
              className={clsx(
                "px-2.5 py-1 rounded-full text-[10px] font-semibold transition-all duration-150 border",
                filter === f.key
                  ? "bg-primary text-white border-primary shadow-xs"
                  : "bg-white/70 text-muted-foreground border-border/50 hover:border-primary/50 hover:text-primary",
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Monthly Breakdown ── */}
      {(isFetching || allMonths.length > 0) && (
        <div className="px-4 py-3 border-b border-border/40">
          <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-2">
            {t("company:payrolls.monthly_breakdown")}
          </p>

          {/* column headers */}
          <div className="flex items-center gap-2 px-1 mb-1">
            <span className="flex-1 text-[9px] text-muted-foreground/60 font-semibold uppercase tracking-wider">
              {t("company:dashboard.period_month")}
            </span>
            <span className="text-[9px] text-muted-foreground/60 font-semibold uppercase tracking-wider w-10 text-center">
              {t("company:subscription.uses")}
            </span>
            <span className="text-[9px] text-muted-foreground/60 font-semibold uppercase tracking-wider w-[72px] text-end">
              {t("company:subscription.time_saved")}
            </span>
          </div>

          <div className="space-y-0.5 max-h-[160px] overflow-y-auto scrollbar-thin">
            {isFetching
              ? Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="flex items-center gap-2 px-1 py-1.5">
                    <Skeleton className="flex-1 h-3 rounded" />
                    <Skeleton className="h-3 w-10 rounded" />
                    <Skeleton className="h-3 w-16 rounded" />
                  </div>
                ))
              : visibleMonths.map((entry, i) => {
                  const human = formatTimeSavedHuman(
                    entry.timeSavedMinutes,
                    workMins,
                  );
                  return (
                    <div
                      key={entry.month}
                      className={clsx(
                        "flex items-center gap-2 px-1 py-1.5 rounded-md transition-colors",
                        i % 2 === 0 ? "bg-muted/30" : "hover:bg-muted/20",
                      )}
                    >
                      <span className="flex-1 text-[12px] font-semibold text-foreground/80 truncate">
                        {formatMonth(entry.month)}
                      </span>
                      <span className="text-[11px] text-muted-foreground tabular-nums w-10 text-center">
                        {entry.uses}
                      </span>
                      <div className="w-[72px] text-end">
                        <span className="text-[12px] font-bold text-primary tabular-nums block">
                          {formatTimeSaved(entry.timeSavedMinutes)}
                        </span>
                        {human && (
                          <span className="text-[9px] text-muted-foreground tabular-nums leading-none">
                            {human}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
          </div>

          {!isFetching && allMonths.length > MONTHS_PREVIEW && (
            <button
              onClick={() => setShowAllMonths((v) => !v)}
              className="mt-1.5 flex w-full items-center justify-center gap-1 py-1 text-[10px] font-semibold text-muted-foreground hover:text-primary transition-colors"
            >
              {showAllMonths
                ? t("company:tasks.show_less")
                : t("common:chrome.more_months", {
                    count: allMonths.length - MONTHS_PREVIEW,
                  })}
            </button>
          )}
        </div>
      )}

      {/* ── Top Time Savers ── */}
      {(saved?.topActions ?? []).length > 0 && (
        <div className="px-4 py-3 border-b border-border/40">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">
              {t("common:chrome.top_time_savers")}
            </p>
            <span className="text-[9px] text-muted-foreground/50 font-medium">
              {t("common:chrome.this_period")}
            </span>
          </div>
          <div className="space-y-1">
            {saved!.topActions.map((action) => {
              const pascal =
                action.iconKey
                  ?.split(/[-_]/)
                  .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
                  .join("") ?? null;
              const lib = LucideIcons as unknown as Record<
                string,
                React.ElementType
              >;
              const Icon =
                (action.iconKey &&
                  (lib[action.iconKey] || (pascal && lib[pascal]))) ||
                Sparkles;
              const human = formatTimeSavedHuman(
                action.timeSavedMinutes,
                workMins,
              );
              return (
                <div
                  key={action.slug}
                  className="flex items-center gap-2.5 px-1 py-1.5 rounded-md hover:bg-muted/30 transition-colors"
                >
                  <div className="size-7 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                    <Icon size={13} className="text-primary" />
                  </div>
                  <span className="flex-1 text-[12px] font-medium text-foreground truncate">
                    {action.name}
                  </span>
                  <div className="text-end shrink-0">
                    <span className="text-[12px] font-bold text-primary tabular-nums block">
                      {formatTimeSaved(action.timeSavedMinutes)}
                    </span>
                    {human && (
                      <span className="text-[9px] text-muted-foreground tabular-nums leading-none">
                        {human}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Footer ── */}
      {onViewDetails && (
        <button
          onClick={onViewDetails}
          className="flex w-full items-center justify-between px-4 py-2.5 text-[11px] font-semibold text-primary hover:bg-primary/5 transition-colors group"
        >
          {t("common:chrome.view_full_ai_usage")}
          <ArrowRight
            size={12}
            className="transition-transform group-hover:translate-x-0.5 rtl-flip"
          />
        </button>
      )}
    </div>
  );
}
